import { createContext, useCallback, useContext, useReducer, useRef, useState } from 'react';
import { ActionTypes, editorReducer, initialState } from './editorReducer';

const EditorContext = createContext(null);

export function EditorProvider({ children }) {
    const [state, dispatch] = useReducer(editorReducer, initialState);

    /* ── DubSync pending-edit bridge (mutable ref, no re-render) ──────
       TimelinePanel writes { lang, cues } here while the edit strip is open.
       handleSave in VideoEditorPage reads it to auto-flush before persisting. */
    const pendingDubSyncEditsRef = useRef(null);
    const setPendingDubSyncEditsRef = useCallback((lang, cues) => {
        pendingDubSyncEditsRef.current = lang ? { lang, cues } : null;
    }, []);

    /* ── Timeline track visibility (UI-only, not persisted) ────────── */
    const [hiddenTimelineLangs, setHiddenTimelineLangs] = useState(new Set());
    const toggleTimelineLang = useCallback((lang) => {
        setHiddenTimelineLangs((prev) => {
            const next = new Set(prev);
            if (next.has(lang)) next.delete(lang);
            else next.add(lang);
            return next;
        });
    }, []);

    /* ── convenience dispatch helpers ──────────────────────────────── */

    const setVideo = useCallback(
        (videoId, videoUrl, videoTitle, videoData) =>
            dispatch({ type: ActionTypes.SET_VIDEO, payload: { videoId, videoUrl, videoTitle, videoData } }),
        [],
    );

    const setDuration = useCallback((d) => dispatch({ type: ActionTypes.SET_DURATION, payload: d }), []);

    const setPlayhead = useCallback((t) => dispatch({ type: ActionTypes.SET_PLAYHEAD, payload: t }), []);

    const play = useCallback(() => dispatch({ type: ActionTypes.PLAY }), []);
    const pause = useCallback(() => dispatch({ type: ActionTypes.PAUSE }), []);

    const setPreviewLang = useCallback(
        (lang) => dispatch({ type: ActionTypes.SET_PREVIEW_LANG, payload: lang }),
        [],
    );

    const toggleSubs = useCallback(() => dispatch({ type: ActionTypes.TOGGLE_SUBS }), []);

    const setTool = useCallback((tool) => dispatch({ type: ActionTypes.SET_TOOL, payload: tool }), []);

    const setZoom = useCallback((z) => dispatch({ type: ActionTypes.SET_ZOOM, payload: z }), []);

    const addCutMarker = useCallback(
        (time) => dispatch({ type: ActionTypes.ADD_CUT_MARKER, payload: time }),
        [],
    );

    const removeCutMarker = useCallback(
        (time) => dispatch({ type: ActionTypes.REMOVE_CUT_MARKER, payload: time }),
        [],
    );

    const clearCutMarkers = useCallback(() => dispatch({ type: ActionTypes.CLEAR_CUT_MARKERS }), []);

    const selectSegment = useCallback(
        (seg) => dispatch({ type: ActionTypes.SELECT_SEGMENT, payload: seg }),
        [],
    );

    const rippleDeleteSegment = useCallback(
        (startTime, endTime, affectedLanguages, affectMusic, affectLogo) =>
            dispatch({
                type: ActionTypes.RIPPLE_DELETE_SEGMENT,
                payload: { startTime, endTime, affectedLanguages, affectMusic, affectLogo },
            }),
        [],
    );

    const addCutMarkerWithSplit = useCallback(
        (time, affectedLanguages) =>
            dispatch({ type: ActionTypes.ADD_CUT_MARKER_WITH_SPLIT, payload: { time, affectedLanguages } }),
        [],
    );

    const loadSubtitles = useCallback(
        (subtitlesByLanguage) =>
            dispatch({ type: ActionTypes.LOAD_SUBTITLES, payload: subtitlesByLanguage }),
        [],
    );

    const loadDubTracks = useCallback(
        (tracks) => dispatch({ type: ActionTypes.LOAD_DUB_TRACKS, payload: tracks }),
        [],
    );

    const addAudioTrack = useCallback(
        (track) => dispatch({ type: ActionTypes.ADD_AUDIO_TRACK, payload: track }),
        [],
    );
    const removeAudioTrack = useCallback(
        (id) => dispatch({ type: ActionTypes.REMOVE_AUDIO_TRACK, payload: id }),
        [],
    );
    const updateAudioTrack = useCallback(
        (id, patch) => dispatch({ type: ActionTypes.UPDATE_AUDIO_TRACK, payload: { id, patch } }),
        [],
    );
    const pinAudioVolume = useCallback(
        (id) => dispatch({ type: ActionTypes.PIN_AUDIO_VOLUME, payload: id }),
        [],
    );
    const normalizeAudio = useCallback(
        () => dispatch({ type: ActionTypes.NORMALIZE_AUDIO }),
        [],
    );
    const toggleMuteVideo = useCallback(
        () => dispatch({ type: ActionTypes.TOGGLE_MUTE_VIDEO }),
        [],
    );

    const setVideoVolume = useCallback((v) => dispatch({ type: ActionTypes.SET_VIDEO_VOLUME, payload: v }), []);
    const setOverlayAudio = useCallback((v) => dispatch({ type: ActionTypes.SET_OVERLAY_AUDIO, payload: v }), []);

    const addLogoTrack = useCallback(
        (track) => dispatch({ type: ActionTypes.ADD_LOGO_TRACK, payload: track }),
        [],
    );
    const removeLogoTrack = useCallback(
        (id) => dispatch({ type: ActionTypes.REMOVE_LOGO_TRACK, payload: id }),
        [],
    );
    const updateLogoTrack = useCallback(
        (id, patch) => dispatch({ type: ActionTypes.UPDATE_LOGO_TRACK, payload: { id, patch } }),
        [],
    );
    const setVideoOffset = useCallback(
        (seconds) => dispatch({ type: ActionTypes.SET_VIDEO_OFFSET, payload: seconds }),
        [],
    );
    const setVideoEndOffset = useCallback(
        (seconds) => dispatch({ type: ActionTypes.SET_VIDEO_END_OFFSET, payload: seconds }),
        [],
    );

    const toggleMediaLibrary = useCallback(
        () => dispatch({ type: ActionTypes.TOGGLE_MEDIA_LIBRARY }),
        [],
    );

    const undo = useCallback(() => dispatch({ type: ActionTypes.UNDO }), []);
    const redo = useCallback(() => dispatch({ type: ActionTypes.REDO }), []);

    const restoreEditorState = useCallback(
        (savedState) => dispatch({ type: ActionTypes.RESTORE_EDITOR_STATE, payload: savedState }),
        [],
    );

    const markSaved = useCallback(
        () => dispatch({ type: ActionTypes.MARK_SAVED }),
        [],
    );

    const addDubSyncTrack = useCallback(
        (language, hasDubbedAudio) =>
            dispatch({ type: ActionTypes.ADD_DUBSYNC_TRACK, payload: { language, hasDubbedAudio } }),
        [],
    );
    const updateDubSyncTrack = useCallback(
        (language, patch) =>
            dispatch({ type: ActionTypes.UPDATE_DUBSYNC_TRACK, payload: { language, patch } }),
        [],
    );
    const removeDubSyncTrack = useCallback(
        (language) => dispatch({ type: ActionTypes.REMOVE_DUBSYNC_TRACK, payload: language }),
        [],
    );
    const setDubSyncCues = useCallback(
        (language, dubSyncCues, originalCues, problematicCues, perCueRatios, rawWhisperCues) =>
            dispatch({
                type: ActionTypes.SET_DUBSYNC_CUES,
                payload: { language, dubSyncCues, originalCues, rawWhisperCues, problematicCues, perCueRatios },
            }),
        [],
    );
    const moveSubtitleCues = useCallback(
        (language, cueId, deltaSeconds, ripple) =>
            dispatch({ type: ActionTypes.MOVE_SUBTITLE_CUES, payload: { language, cueId, deltaSeconds, ripple } }),
        [],
    );

    const updateDubSyncCues = useCallback(
        (language, cues) =>
            dispatch({ type: ActionTypes.UPDATE_DUBSYNC_CUES, payload: { language, cues } }),
        [],
    );

    const value = {
        state,
        dispatch,
        // helpers
        setVideo,
        setDuration,
        setPlayhead,
        play,
        pause,
        setPreviewLang,
        toggleSubs,
        setTool,
        setZoom,
        addCutMarker,
        removeCutMarker,
        clearCutMarkers,
        selectSegment,
        rippleDeleteSegment,
        addCutMarkerWithSplit,
        loadSubtitles,
        loadDubTracks,
        addAudioTrack,
        removeAudioTrack,
        updateAudioTrack,
        pinAudioVolume,
        normalizeAudio,
        toggleMuteVideo,
        setVideoVolume,
        setOverlayAudio,
        addLogoTrack,
        removeLogoTrack,
        updateLogoTrack,
        setVideoOffset,
        setVideoEndOffset,
        toggleMediaLibrary,
        undo,
        redo,
        restoreEditorState,
        markSaved,
        hiddenTimelineLangs,
        toggleTimelineLang,
        // DubSync
        addDubSyncTrack,
        updateDubSyncTrack,
        removeDubSyncTrack,
        setDubSyncCues,
        moveSubtitleCues,
        updateDubSyncCues,
        // DubSync pending-edit bridge
        pendingDubSyncEditsRef,
        setPendingDubSyncEditsRef,
    };

    return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

export function useEditor() {
    const ctx = useContext(EditorContext);
    if (!ctx) throw new Error('useEditor must be used inside <EditorProvider>');
    return ctx;
}
